import { type QBOBillSyncReport, type QBOBillSyncResult } from '@kalos/kalos-rpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingIcon,
  Skeleton,
} from '@kalos/ui';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadErrorState } from '../../../../components/LoadErrorState';
import { StatusPill, type StatusPillTone } from '../../../../components/StatusPill';
import { Flagged } from '../../../../featureFlags/Flagged';
import { useFeatureFlag } from '../../../../featureFlags/useFeatureFlag';
import { useQBOBillSyncMutation } from '../../../../hooks/react-query/useQBOBillSyncMutation';

type Step =
  | { kind: 'loading'; count?: number }
  | { kind: 'empty' }
  | { kind: 'preview'; transactionIds: number[]; report: QBOBillSyncReport }
  | { kind: 'posting'; transactionIds: number[]; report: QBOBillSyncReport }
  | { kind: 'posted'; report: QBOBillSyncReport }
  | { kind: 'error'; title: string; message: string };

type Props = {
  onClose: () => void;
  /** The ids of every transaction matching the Accounts Payable page's filters. */
  loadTransactionIds: () => Promise<number[]>;
  adminId: number;
};

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const errorMessage = (err: unknown) =>
  err instanceof Error && err.message ? err.message : 'Check your connection, then try again.';

// Transactions sharing a vendor, invoice number and direction post as one
// Bill or Vendor Credit, the way kalos-core's groupAPBillRows groups them.
const countEntries = (results: QBOBillSyncResult[]) => {
  const bills = new Set<string>();
  const credits = new Set<string>();
  for (const r of results) {
    if (r.status !== 'created') continue;
    const invoice = r.invoiceNumber.trim();
    const vendor = r.vendor.toLowerCase().split(/\s+/).filter(Boolean).join(' ');
    const key = invoice ? `${vendor}\u0000${invoice}` : `#${r.transactionId}`;
    (r.txnType === 'VendorCredit' ? credits : bills).add(key);
  }
  return { bills: bills.size, credits: credits.size };
};

const describeEntries = ({ bills, credits }: { bills: number; credits: number }) =>
  [
    bills > 0 && plural(bills, 'bill', 'bills'),
    credits > 0 && plural(credits, 'vendor credit', 'vendor credits'),
  ]
    .filter(Boolean)
    .join(' and ');

const STATUS_ORDER: Record<string, number> = { failed: 0, created: 1, skipped: 2 };

const statusPill = (status: string, posted: boolean): { tone: StatusPillTone; label: string } => {
  switch (status) {
    case 'created':
      return { tone: 'success', label: posted ? 'Posted' : 'Will post' };
    case 'failed':
      return { tone: 'warning', label: posted ? 'Not posted' : "Won't post" };
    default:
      return { tone: 'muted', label: 'Skipped' };
  }
};

const ResultRow: FC<{ result: QBOBillSyncResult; posted: boolean }> = ({ result, posted }) => {
  const pill = statusPill(result.status, posted);
  const vendor =
    result.qboVendor && result.qboVendor !== result.vendor
      ? `${result.vendor} → ${result.qboVendor}`
      : result.vendor;
  return (
    <li className="flex flex-col gap-1 py-2">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="font-medium">
          {result.invoiceNumber ? `Invoice ${result.invoiceNumber}` : 'No invoice number'}
          {vendor && <span className="text-muted-foreground font-normal"> · {vendor}</span>}
        </span>
        <span className="flex items-center gap-2">
          <span className="tabular-nums">{money.format(result.amount)}</span>
          {result.txnType === 'VendorCredit' && (
            <span className="text-muted-foreground text-xs">Vendor credit</span>
          )}
          <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
        </span>
      </div>
      {result.detail && <p className="text-muted-foreground text-sm">{result.detail}</p>}
      <p className="text-muted-foreground text-xs">
        Transaction {result.transactionId}
        {result.qbTxnId && ` · QuickBooks ID ${result.qbTxnId}`}
        {result.errorCode && ` · QuickBooks error ${result.errorCode}`}
      </p>
    </li>
  );
};

const ReportBody: FC<{ report: QBOBillSyncReport; posted: boolean }> = ({ report, posted }) => {
  const entries = describeEntries(countEntries(report.results));
  const results = useMemo(
    () =>
      [...report.results].sort(
        (a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3),
      ),
    [report.results],
  );
  return (
    <div className="flex flex-col gap-4">
      <ul className="ml-5 list-disc text-sm">
        <li>
          {posted ? 'Posted ' : 'Would post '}
          <strong>{plural(report.succeeded, 'transaction', 'transactions')}</strong>
          {entries && ` as ${entries}`}
          {posted && ', now marked Processed'}
        </li>
        <li>
          Skipped <strong>{report.skipped}</strong>: already in QuickBooks, already Processed, or
          not an Accounts Payable transaction
        </li>
        <li>
          {posted ? 'Did not post ' : "Won't post "}
          <strong>{report.failed}</strong>
          {posted
            ? ': fix what each one says, then preview again. Posted transactions are skipped next time.'
            : ': fix what each one says, then preview again'}
        </li>
      </ul>
      {report.unmappedVendors.length > 0 && (
        <div className="text-sm">
          <p className="font-medium">
            {plural(report.unmappedVendors.length, 'vendor has', 'vendors have')} no QuickBooks
            vendor:
          </p>
          <p>{report.unmappedVendors.join(', ')}</p>
          <p className="text-muted-foreground">
            Rename the vendor in Kalos to match QuickBooks, or ask engineering to map it.
          </p>
        </div>
      )}
      {report.unmappedCategories.length > 0 && (
        <div className="text-sm">
          <p className="font-medium">
            {plural(report.unmappedCategories.length, 'category has', 'categories have')} no
            QuickBooks account: {report.unmappedCategories.join(', ')}
          </p>
          <p className="text-muted-foreground">Ask engineering to add the mapping.</p>
        </div>
      )}
      {report.unmappedJobNumbers.length > 0 && (
        <div className="text-sm">
          <p className="font-medium">
            {plural(report.unmappedJobNumbers.length, 'job has', 'jobs have')} no QuickBooks
            sub-customer: {report.unmappedJobNumbers.join(', ')}
          </p>
          <p className="text-muted-foreground">
            Those lines post without a customer. Map them on the{' '}
            <a
              className="text-link underline"
              href="/quickbooks-customer-mapping"
              target="_blank"
              rel="noreferrer"
            >
              QuickBooks Customer Mapping
            </a>{' '}
            page{posted ? '.' : ' first if they need one.'}
          </p>
        </div>
      )}
      <ul className="divide-border divide-y border-y">
        {results.map((r) => (
          <ResultRow key={r.transactionId} result={r} posted={posted} />
        ))}
      </ul>
    </div>
  );
};

/**
 * The Accounts Payable direct QuickBooks sync. Mounting it previews every
 * transaction matching the page's filters (a dry run: nothing is posted or
 * changed). With qbo.ap-bill-sync on, the preview can then be posted; the
 * real run sends exactly the previewed ids, so what was reviewed is what posts.
 */
export const QuickBooksBillSyncDialog: FC<Props> = ({ onClose, loadTransactionIds, adminId }) => {
  // Without qbo.ap-bill-sync the dialog previews and never posts.
  const canPost = useFeatureFlag('qbo.ap-bill-sync');
  const [step, setStep] = useState<Step>({ kind: 'loading' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutateAsync } = useQBOBillSyncMutation();

  const preview = useCallback(async () => {
    setStep({ kind: 'loading' });
    try {
      const transactionIds = await loadTransactionIds();
      if (transactionIds.length === 0) {
        setStep({ kind: 'empty' });
        return;
      }
      setStep({ kind: 'loading', count: transactionIds.length });
      const report = await mutateAsync({ transactionIds, dryRun: true, adminId });
      setStep({ kind: 'preview', transactionIds, report });
    } catch (err) {
      console.error('QuickBooks bill preview failed', err);
      setStep({
        kind: 'error',
        title: "Couldn't preview the QuickBooks bills",
        message: errorMessage(err),
      });
    }
  }, [loadTransactionIds, mutateAsync, adminId]);

  // The parent mounts the dialog to open it, so one preview per opening.
  const previewed = useRef(false);
  useEffect(() => {
    if (previewed.current) return;
    previewed.current = true;
    void preview();
  }, [preview]);

  const post = useCallback(async () => {
    if (step.kind !== 'preview') return;
    setConfirmOpen(false);
    setStep({ ...step, kind: 'posting' });
    try {
      const report = await mutateAsync({
        transactionIds: step.transactionIds,
        dryRun: false,
        adminId,
      });
      setStep({ kind: 'posted', report });
    } catch (err) {
      console.error('QuickBooks bill sync failed', err);
      setStep({
        kind: 'error',
        title: "Couldn't post to QuickBooks",
        message: `${errorMessage(err)} Only transactions QuickBooks accepted are marked Processed. Preview again to see where things stand.`,
      });
    }
  }, [step, mutateAsync, adminId]);

  const posting = step.kind === 'posting';
  const handleOpenChange = (next: boolean) => {
    if (!next && !posting) onClose();
  };

  const pendingReport = step.kind === 'preview' || step.kind === 'posting' ? step.report : null;
  const toPost = pendingReport?.succeeded ?? 0;
  const entries = pendingReport ? describeEntries(countEntries(pendingReport.results)) : '';

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] max-w-3xl flex-col gap-4 overflow-y-auto"
        showClose={!posting}
        disableOutsideClick={posting}
        onEscapeKeyDown={(e) => posting && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {step.kind === 'posted' ? 'Posted to QuickBooks' : 'Preview QuickBooks bills'}
          </DialogTitle>
          <DialogDescription>
            {step.kind === 'posted'
              ? 'QuickBooks now has the bills below, and their transactions are Processed.'
              : 'Nothing has been sent to QuickBooks. This shows what posting the transactions that match your filters would do.'}
          </DialogDescription>
        </DialogHeader>

        {step.kind === 'loading' && (
          <div className="flex flex-col gap-2" role="status">
            <p className="text-sm">
              {step.count
                ? `Checking ${plural(step.count, 'transaction', 'transactions')} against QuickBooks…`
                : 'Loading the transactions that match your filters…'}
            </p>
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {step.kind === 'empty' && (
          <p className="text-sm">
            No transactions match the filters on this page. Change the filters, then preview again.
          </p>
        )}

        {step.kind === 'error' && (
          <LoadErrorState
            title={step.title}
            description={step.message}
            retryLabel="Preview again"
            onRetry={preview}
            isRetrying={false}
          />
        )}

        {(step.kind === 'preview' || step.kind === 'posting') && (
          <ReportBody report={step.report} posted={false} />
        )}
        {step.kind === 'posted' && <ReportBody report={step.report} posted />}

        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={posting} onClick={onClose}>
            Close
          </Button>
          {canPost && pendingReport && toPost > 0 && (
            <Flagged flag="qbo.ap-bill-sync">
              <Button disabled={posting} onClick={() => setConfirmOpen(true)}>
                {posting && <LoadingIcon className="mr-2" />}
                {posting ? 'Posting to QuickBooks…' : `Post ${entries} to QuickBooks`}
              </Button>
            </Flagged>
          )}
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Post {entries} to QuickBooks?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates them in QuickBooks and marks their{' '}
              {plural(toPost, 'transaction', 'transactions')} Processed. Undoing it means deleting
              them in QuickBooks by hand.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={post}>Post to QuickBooks</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
